import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaDatabase, FaTools, FaUsers } from 'react-icons/fa';
import './Skills.css';
import { API_BASE_URL } from '../config';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('programming');
  const [skills, setSkills] = useState({});
  const [loading, setLoading] = useState(true);

  const skillCategories = [
    { id: 'programming', name: 'Programming', icon: FaCode },
    { id: 'frameworks', name: 'Frameworks', icon: FaCode },
    { id: 'databases', name: 'Databases', icon: FaDatabase },
    { id: 'tools', name: 'Tools', icon: FaTools },
    { id: 'soft-skills', name: 'Soft Skills', icon: FaUsers }
  ];

  // Default skills data as fallback
  const defaultSkills = useMemo(() => ({
    programming: [
      { name: 'JavaScript', proficiency: 70, years: 0 },
      { name: 'React', proficiency: 65, years: 0 },
      { name: 'Node.js', proficiency: 60, years: 0 },
      { name: 'HTML/CSS', proficiency: 75, years: 0 },
      { name: 'Python', proficiency: 70, years: 0 },
      { name: 'MongoDB', proficiency: 55, years: 0 }
    ],
    frameworks: [
      { name: 'Express.js', proficiency: 55, years: 0 },
      { name: 'React Router', proficiency: 50, years: 0 },
      { name: 'TensorFlow', proficiency: 45, years: 0 },
      { name: 'Pandas', proficiency: 50, years: 0 },
      { name: 'NumPy', proficiency: 45, years: 0 }
    ],
    databases: [
      { name: 'MongoDB', proficiency: 45, years: 0 },
      { name: 'MySQL', proficiency: 40, years: 0 },
      { name: 'SQLite', proficiency: 35, years: 0 },
      { name: 'PostgreSQL', proficiency: 30, years: 0 }
    ],
    tools: [
      { name: 'Git', proficiency: 60, years: 0 },
      { name: 'VS Code', proficiency: 75, years: 0 },
      { name: 'Jupyter Notebook', proficiency: 50, years: 0 },
      { name: 'Postman', proficiency: 45, years: 0 },
      { name: 'npm/yarn', proficiency: 55, years: 0 }
    ],
    'soft-skills': [
      { name: 'Leadership', proficiency: 60, years: 0 },
      { name: 'Communication', proficiency: 65, years: 0 },
      { name: 'Problem Solving', proficiency: 70, years: 0 },
      { name: 'Team Work', proficiency: 65, years: 0 },
      { name: 'Time Management', proficiency: 60, years: 0 }
    ]
  }), []);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/skills`);
        if (response.ok) {
          const data = await response.json();
          // Group skills by category
          const groupedSkills = data.reduce((acc, skill) => {
            if (!acc[skill.category]) {
              acc[skill.category] = [];
            }
            acc[skill.category].push({
              name: skill.name,
              proficiency: skill.proficiency,
              years: skill.yearsOfExperience || 0
            });
            return acc;
          }, {});
          setSkills(groupedSkills);
        } else {
          // Fallback to default skills if API fails
          setSkills(defaultSkills);
        }
      } catch (error) {
        console.error('Error fetching skills:', error);
        // Fallback to default skills
        setSkills(defaultSkills);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();
  }, [defaultSkills]);

  return (
    <section className="skills section" id="skills">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Skills</h2>
          <p className="section-subtitle">My technical & miscellaneous skills</p>
        </motion.div>

        <motion.div
          className="skills-categories"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {skillCategories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              <category.icon />
              <span>{category.name}</span>
            </button>
          ))}
        </motion.div>

        {loading ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="loading-spinner"></div>
            <p>Loading skills...</p>
          </motion.div>
        ) : (
          <motion.div
            className="skills-content"
            key={activeCategory}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="skills-grid">
              {skills[activeCategory]?.map((skill, index) => (
                <motion.div
                  key={skill.name}
                  className="skill-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="skill-header">
                    <h3 className="skill-name">{skill.name}</h3>
                    <span className="skill-years">{skill.years}+ Years XP</span>
                  </div>
                  
                  <div className="skill-progress">
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.proficiency}%` }}
                        transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      />
                    </div>
                    <span className="progress-text">{skill.proficiency}%</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div
          className="skills-note"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <p>
            All of my experience has been acquired through hands-on project development 
            and rigorous academic pursuits.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 